import * as React from "react";
import { Box, Grid } from "@mui/material";
import CclInfoTable, {
    CclInfoTableRowTemplate,
    CclInfoTableFormValue,
} from "../../../../components/common/cclInfoTable/cclInfoTable";
import { CclInfoTblResourceInfoTemplate } from "./cclInfoTblResourceInfoTemplate";
import {
    Profile,
    UpdateCclManagedProfilePropertiesPayload,
} from "../../../../services/types/enterpriseCoachingApiTypes";
import { flattenObject } from "../../../../utilities/flattenObject";
import ComponentLoader from "../../../../components/common/componentLoader";
import {
    useGetAllProfileReportingLocationsQuery,
    useUpdateCclManagedProfilePropertiesMutation,
    useGetAllProfileReportingRegionsQuery,
    useGetRolesByProfileKeyQuery,
    useGetFeeStructuresQuery,
    useGetStatesandProvincesByCMKeyQuery,
} from "../../../../services/cclTokenedEnterpriseCoachingApi";
import { eInfoTableValueFieldEditType } from "../../../../components/common/cclInfoTable/cclInfoTableValueField";
import { AccessEventIndividualDetails } from "../../../../services/types/accessEventTypes";
import { CclInfoTblMyInfoAdminEditTemplate } from "../../../my-access/accessDetail/myInfoDetail/cclInfoTblMyInfoAdminEditTemplate";
import {
    eGroupIds,
    eRoleIds,
    UserClaimsService,
} from "../../../../services/currentUserService/currentUserService";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import ProfileImage from "../../../my-access/accessDetail/myInfoDetail/profileImage";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import CclGenericAccordian from "../../../../components/common/cclGenericAccordian";
import { useGetCclUserAccountDetailQuery } from "../../../../services/cclTokenedGrandCentralApi";
import RolesDataGrid from "./rolesDataGrid";
import { useGetResourceByEmailQuery } from "../../../../services/cclTokenedResourceApi";
import CclAlertSnackbar from "../../../../components/common/cclAlertSnackbar";
import { CclInfoTblManagmentInfoTemplate } from "./cclInfoTblManagementInfoTemplate";
import { getLocalIsoDateWithNoTime, parseDateAsLocal } from "../../../../utilities/dateHelpers";

interface ResourceInfoPanelProps {
    pKey: string;
    profile: Profile;
    refetchProfile: () => void;
}

const ResourceInfoPanel: React.FC<ResourceInfoPanelProps> = (props) => {
    const claimService = new UserClaimsService();
    const [picture, setPicture] = React.useState<string>("");
    const [name, setName] = React.useState<string>("");
    const [modTemplate, setModTemplate] = React.useState<CclInfoTableRowTemplate[]>([]);
    const [profileTemplate, setProfileTemplate] = React.useState<CclInfoTableRowTemplate[]>([]);
    const [managementTemplate, setManagementTemplate] = React.useState<CclInfoTableRowTemplate[]>(
        [],
    );
    const [openSnackbar, setOpenSnackbar] = React.useState<boolean>(false);

    const { data: ReportingLocationList } = useGetAllProfileReportingLocationsQuery();
    const { data: feeStructures } = useGetFeeStructuresQuery();
    const [updateManagedProperties, { isError, isLoading }] =
        useUpdateCclManagedProfilePropertiesMutation();
    const { logEvent } = useLogAccessEvent();
    const { data: reportingRegion } = useGetAllProfileReportingRegionsQuery();
    const { data: enterpriseData } = useGetCclUserAccountDetailQuery(props.profile.email, {
        skip: props.profile.email == null || props.profile.email === "",
    });
    const { data: rolesData, isLoading: isRoleDataLoading } = useGetRolesByProfileKeyQuery(
        props.pKey,
    );
    const { data: resource } = useGetResourceByEmailQuery(props.profile.email, {
        skip: props.profile.email == null || props.profile.email === "",
    });

    const [cmKey, setCmKey] = React.useState<number>(props.profile.address.cmKey ?? 0);
    
    const { data: stateList, refetch: fetch } = useGetStatesandProvincesByCMKeyQuery(cmKey);

    const labelStyle = "original";
    const displayValue = "UTC";
    const timezones = {
        ...allTimezones,
    };

    const { options } = useTimezoneSelect({ labelStyle, displayValue, timezones });

    let showFeeStructure = false;
    let editFeeStructure = false;

    if (claimService.IsUserSuperAdmin()) {
        showFeeStructure = true;
        editFeeStructure = true;
    } else if (claimService.IsUserInGroups([eGroupIds.eCCLAdmin])) {
        showFeeStructure = true;
        if (claimService.IsUserInAppRole(eRoleIds.eResourceManager)) {
            editFeeStructure = true;
        }
    }

    React.useEffect(() => {

        var newTemplate = CclInfoTblResourceInfoTemplate;

        var rowitem = newTemplate.find((r) => r.id === "fullName");
        rowitem = newTemplate.find((r) => r.id === "imKey");
        if (rowitem && rowitem.valueField) {
            rowitem.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
            rowitem.valueField.url = `/user?email=${encodeURIComponent(
                props.profile.email,
            )}&imkey=${props.profile.imKey}`;
        }

        newTemplate = CclInfoTblManagmentInfoTemplate;

        if (reportingRegion != null && reportingRegion.length > 0) {
            let repLoc = [...reportingRegion];
            repLoc.sort((a, b) => a.name.localeCompare(b.name));
            rowitem = newTemplate.find((r) => r.id === "prrKey");
            if (rowitem) {
                if (rowitem.valueField) {
                    rowitem.valueField.selectOptions = repLoc.map((c) => {
                        return { val: c.prrKey.toString(), option: c.name };
                    });
                }
                rowitem.editable =
                    (claimService.IsUserCclAdminOrSuperAdmin() &&
                        claimService.IsUserInAppRole(eRoleIds.eResourceManager)) ||
                    claimService.IsUserSuperAdmin();
            }
        }

        if (typeof ReportingLocationList !== "undefined" && ReportingLocationList.length > 0) {
            rowitem = newTemplate.find((r) => r.id === "prlKey");
            let repLoc = [...ReportingLocationList];
            repLoc.sort((a, b) => a.name.localeCompare(b.name));
            if (rowitem) {
                if (rowitem.valueField) {
                    rowitem.valueField.selectOptions = repLoc.map((l) => {
                        return { val: l.prlKey.toString(), option: l.description };
                    });
                }
                rowitem.editable =
                    (claimService.IsUserCclAdminOrSuperAdmin() &&
                        claimService.IsUserInAppRole(eRoleIds.eResourceManager)) ||
                    claimService.IsUserSuperAdmin();
            }
        }

        if (showFeeStructure) {
            if (typeof feeStructures !== "undefined" && feeStructures.length > 0) {
                rowitem = newTemplate.find((r) => r.id === "pfsKey");
                let repLoc = [...feeStructures];
                repLoc.sort((a, b) => a.name.localeCompare(b.name));
                if (rowitem) {
                    if (rowitem.valueField) {
                        rowitem.valueField.selectOptions = repLoc.map((l) => {
                            return { val: l.pfsKey.toString(), option: l.name };
                        });
                    }
                    rowitem.editable = editFeeStructure;
                }
            }
        }

        rowitem = newTemplate.find((r) => r.id === "cclstartDate");
        if (rowitem) {
            rowitem.editable =
                (claimService.IsUserCclAdminOrSuperAdmin() &&
                    claimService.IsUserInAppRole(eRoleIds.eResourceManager)) ||
                claimService.IsUserSuperAdmin();
        }

        setManagementTemplate([...newTemplate]);
        // eslint-disable-next-line
    }, [ReportingLocationList, reportingRegion, feeStructures]);

    React.useEffect(() => {
        const enterpriseDetail = enterpriseData?.find(
            (e) => e.email.toLowerCase() === props.profile.email.toLowerCase(),
        );

        setCmKey(props.profile.address.cmKey ?? 0);
        if (cmKey !== 0) {
            fetch();
        }

        var newTemplate = CclInfoTblResourceInfoTemplate;
        var rowitem = newTemplate.find((r) => r.id === "ceContactId");
        if (typeof rowitem !== "undefined" && rowitem !== null) {
            rowitem.valueField.value = enterpriseDetail?.ceContactId ?? "";
        }

        rowitem = newTemplate.find((r) => r.id === "bookableresourceid");
        if (typeof rowitem !== "undefined" && rowitem !== null && resource) {
            rowitem.valueField.value =
                resource[0].bookableresourceid ?? "This user has no bookable resource id";
        }

        rowitem = newTemplate.find((r) => r.id === "cclstartDate");
        if (typeof rowitem !== "undefined" && rowitem !== null) {
            if (props.profile.cclStartDate != null) {
                rowitem.valueField.value = parseDateAsLocal(
                    new Date(props.profile.cclStartDate),
                ).toLocaleDateString();
            }
        }

        setModTemplate([...newTemplate]);

        newTemplate = CclInfoTblMyInfoAdminEditTemplate;
        rowitem = newTemplate.find((r) => r.id === "fullName");
        if (typeof rowitem !== "undefined" && rowitem !== null) {
            rowitem.valueField.editFieldType = eInfoTableValueFieldEditType.Link;
            rowitem.valueField.url = `/user?email=${encodeURIComponent(
                props.profile.email,
            )}&imkey=${props.profile.imKey}`;
        }
        if (rowitem && rowitem.valueField) {
            rowitem.valueField.value = props.profile.firstName + " " + props.profile.lastName;
        }
        if (!showFeeStructure) {
            newTemplate = newTemplate.filter((r) => r.id !== "pfsKey");
        }

        if (stateList === null || stateList === undefined || stateList.length === 0) {
            rowitem = newTemplate.find((r) => r.id === "stateOrProvinceName");
            if (typeof rowitem !== "undefined" && rowitem !== null) {
                rowitem.valueField.value = " ";
            }
        } else if (stateList && stateList.length > 0) {
            rowitem = newTemplate.find((r) => r.id === "stateOrProvinceName");
            if (rowitem) {
                if (rowitem.valueField) {
                        rowitem.valueField.value = props.profile.address.stateOrProvinceName;
                };
            }
        }

        setProfileTemplate([...newTemplate]);

        if (!props.profile) {
            return;
        }

        rowitem = newTemplate.find((r) => r.id === "timeZoneId");
        if (rowitem && rowitem.valueField && props.profile.ianaTimeZoneId != null) {
            for (let i = 0; i < options.length; i++) {
                if (options[i].value.toLowerCase() === props.profile.ianaTimeZoneId.toLowerCase()) {
                    rowitem.valueField.value = options[i].label;
                    break;
                } else {
                    rowitem.valueField.value = props.profile.ianaTimeZoneId;
                }
            }
        }

        setPicture(props.profile ? props.profile.profilePictureUrl : "");
        setName(props.profile.firstName + " " + props.profile.lastName);
        // eslint-disable-next-line
    }, [props.profile, enterpriseData, resource, stateList]);

    const handleManagementInfoSave = (changes: CclInfoTableFormValue[]) => {
        const newPrrKey = changes.find((c) => c.name === "prrKey");
        const newPrlKey = changes.find((c) => c.name === "prlKey");
        const newPfsKey = changes.find((c) => c.name === "pfsKey");
        const newStartDate = changes.find((c) => c.name === "cclstartDate");

        if (newPrrKey == null && newPrlKey == null && newPfsKey == null && newStartDate == null)
            return;

        const payload: UpdateCclManagedProfilePropertiesPayload = {
            imKey: props.profile.imKey,
            payload: {
                pKey: +props.pKey,
                imKey: props.profile.imKey,
                prlKey: newPrlKey?.value?.val != null ? +newPrlKey.value.val : undefined,
                prrKey: newPrrKey?.value?.val != null ? +newPrrKey.value.val : undefined,
                pfsKey: newPfsKey?.value?.val != null ? +newPfsKey.value.val : undefined,
                cclStartDate:
                    newStartDate?.value != null
                        ? getLocalIsoDateWithNoTime(new Date(newStartDate.value))
                        : undefined,
            },
        };

        updateManagedProperties(payload)
            .unwrap()
            .then(() => {
                setOpenSnackbar(true);
                const evtData: AccessEventIndividualDetails = {
                    imKey: props.profile.imKey.toString(),
                    email: props.profile.email,
                };

                logEvent("ResourceDetailsEdited", evtData);
            });
    };

    const checkReadOnly = () => {
        if (claimService.IsUserSuperAdmin()) return false;
        if (claimService.IsUserInGroups([eGroupIds.eCCLAdmin]) && claimService.IsUserInAppRole(10))
            return false;
        return true;
    };

    if (modTemplate.length > 0) {
        return (
            <Grid container spacing={4}>
                <Grid item xs={12} md={7} height={"100%"}>
                    <CclGenericAccordian headingText={"Profile"}>
                        <>
                            <Box style={{ display: "flex", flexDirection: "row" }}>
                                <ProfileImage
                                    name={name}
                                    picture={picture}
                                    refetch={props.refetchProfile}
                                    imKey={`${props.profile.imKey}`}
                                    restrictAccess={true}
                                    fromResources={true}
                                    email={props.profile.email}
                                />
                            </Box>
                            <Box sx={{ m: 2, borderRadius: 3 }}>
                                {reportingRegion &&
                                    reportingRegion.length > 0 &&
                                    ReportingLocationList &&
                                    ReportingLocationList?.length > 0 && (
                                        <CclInfoTable
                                            template={profileTemplate}
                                            data={flattenObject(props.profile)}
                                            readonly={true}
                                        />
                                    )}
                            </Box>
                        </>
                    </CclGenericAccordian>
                </Grid>
                <Grid item xs={12} md={5} height={"100%"}>
                    <CclGenericAccordian headingText={"Management Info."} sx={{mb: 2}}>
                        {ReportingLocationList && ReportingLocationList?.length > 0 && (
                            <Box sx={{ m: 2, borderRadius: 3 }}>
                                <CclInfoTable
                                    template={managementTemplate}
                                    onSave={handleManagementInfoSave}
                                    data={flattenObject(props.profile)}
                                    readonly={checkReadOnly()}
                                    saving={isLoading}
                                />
                            </Box>
                        )}
                    </CclGenericAccordian>
                    <CclGenericAccordian headingText={"D365 Records"} sx={{mb: 2}}>
                        {ReportingLocationList && ReportingLocationList?.length > 0 && (
                            <Box sx={{ m: 2, borderRadius: 3 }}>
                                <CclInfoTable
                                    template={modTemplate}
                                    data={flattenObject(props.profile)}
                                    saving={isLoading}
                                />
                            </Box>
                        )}
                    </CclGenericAccordian>
                    <CclGenericAccordian headingText={"D365 Roles"} sx={{mb: 2}}>
                        <RolesDataGrid isLoading={isRoleDataLoading} data={rolesData} />
                    </CclGenericAccordian>
                </Grid>
                <CclAlertSnackbar
                    message={isError ? "Profile Update Failed" : "Profile Updated"}
                    severity={isError ? "error" : "success"}
                    open={openSnackbar}
                    onClose={() => {
                        setOpenSnackbar(false);
                    }}
                />
            </Grid>
        );
    }
    return <ComponentLoader msg={"Loading"} />;
};

export default ResourceInfoPanel;
